import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Header from './Header';
import {
  Building2,
  Phone,
  Mail,
  MapPin,
  Image,
  Upload,
  PlusCircle,
  CheckCircle,
  AlertCircle,
  X,
  Globe
} from 'lucide-react';

const ModalCreateCompanyForm = () => {
  const server_app_api_php = window.SERVER_APP_API_PHP;
  const [companyName, setCompanyName] = useState("");
  const [companyPhone, setCompanyPhone] = useState("");
  const [companyEmail, setCompanyEmail] = useState("");
  const [companyAddress, setCompanyAddress] = useState("");
  const [companyLogo, setCompanyLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });

  useEffect(() => {
    const isFormValid = companyName && companyPhone && companyEmail && companyAddress;
  }, [companyName, companyPhone, companyEmail, companyAddress]);

  const handleInputChange = (e) => {
    const { id, value, files } = e.target;
    if (id === "companyLogo") {
      const file = files[0];
      setCompanyLogo(file);
      if (file) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setLogoPreview(reader.result);
        };
        reader.readAsDataURL(file);
      } else {
        setLogoPreview(null);
      }
    } else {
      switch (id) {
        case "companyName":
          setCompanyName(value);
          break;
        case "companyPhone":
          setCompanyPhone(value);
          break;
        case "companyEmail":
          setCompanyEmail(value);
          break;
        case "companyAddress":
          setCompanyAddress(value);
          break;
        default:
          break;
      }
    }
  };

  const removeLogo = () => {
    setCompanyLogo(null);
    setLogoPreview(null);
    document.getElementById('companyLogo').value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setSubmitStatus({ type: '', message: '' });

    const formData = new FormData();
    formData.append("companyName", companyName);
    formData.append("companyPhone", companyPhone);
    formData.append("companyEmail", companyEmail);
    formData.append("companyAddress", companyAddress);
    if (companyLogo) {
      formData.append("companyLogo", companyLogo);
    }
    formData.append("apiType", 'registerCompany');

    try {
      const response = await axios.post(`${server_app_api_php}/server/index.php`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      const data = response.data;
      if (data === 'Upload Completed Successfully' || data === '') {
        setSubmitStatus({ 
          type: 'success', 
          message: 'Company registered successfully!' 
        });
        // Reset form
        setCompanyName("");
        setCompanyPhone("");
        setCompanyEmail("");
        setCompanyAddress("");
        setCompanyLogo(null);
        setLogoPreview(null);
      } else {
        setSubmitStatus({ 
          type: 'error', 
          message: data 
        });
      }
    } catch (error) {
      setSubmitStatus({ 
        type: 'error', 
        message: 'Error: Please check your internet connection. If this persists, contact support.' 
      });
    } finally {
      setIsLoading(false);
    }
  };

  const isFormValid = companyName && companyPhone && companyEmail && companyAddress;

  return (
    <>
      <Header />
      
      <main className="min-h-screen bg-gradient-to-b from-gray-50 to-white pt-16 pl-64">
        <div className="p-8">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center space-x-4 mb-6">
              <div className="p-3 bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl">
                <Building2 className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Register Company</h1>
                <p className="text-gray-600 mt-2">Create a new company profile for your real estate business</p>
              </div>
            </div>

            {/* Progress Steps */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200 mb-8">
              <div className="flex items-center justify-between">
                {['Company Details', 'Contact Info', 'Logo Upload'].map((step, index) => (
                  <div key={index} className="flex items-center">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      index === 0 
                        ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white' 
                        : 'bg-gray-100 text-gray-400'
                    }`}>
                      {index + 1}
                    </div>
                    <span className={`ml-2 ${index === 0 ? 'font-medium text-gray-900' : 'text-gray-500'}`}>
                      {step}
                    </span>
                    {index < 2 && <div className="w-16 h-0.5 bg-gray-200 mx-4"></div>}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Registration Form */}
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
              {/* Form Header */}
              <div className="px-8 py-6 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
                <div className="flex items-center space-x-3">
                  <PlusCircle className="w-6 h-6 text-blue-500" />
                  <h2 className="text-xl font-bold text-gray-900">New Company Profile</h2>
                </div>
                <p className="text-gray-600 mt-2">Enter company details below to create a new profile</p>
              </div>

              {/* Form Content */}
              <div className="p-8">
                {submitStatus.message && (
                  <div className={`mb-6 p-4 rounded-xl ${
                    submitStatus.type === 'success' 
                      ? 'bg-green-50 border border-green-200' 
                      : 'bg-red-50 border border-red-200'
                  }`}>
                    <div className="flex items-center">
                      {submitStatus.type === 'success' ? (
                        <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                      ) : (
                        <AlertCircle className="w-5 h-5 text-red-500 mr-3" />
                      )}
                      <p className={submitStatus.type === 'success' ? 'text-green-700' : 'text-red-700'}>
                        {submitStatus.message}
                      </p>
                    </div>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Company Name */}
                    <div className="md:col-span-2">
                      <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-2">
                        <div className="flex items-center">
                          <Building2 className="w-4 h-4 mr-2 text-gray-400" />
                          Company Name *
                        </div>
                      </label>
                      <input
                        type="text"
                        id="companyName"
                        value={companyName}
                        onChange={handleInputChange}
                        placeholder="Enter company legal name"
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                        required
                      />
                    </div>

                    {/* Phone */}
                    <div>
                      <label htmlFor="companyPhone" className="block text-sm font-medium text-gray-700 mb-2">
                        <div className="flex items-center">
                          <Phone className="w-4 h-4 mr-2 text-gray-400" />
                          Phone Number *
                        </div>
                      </label>
                      <input
                        type="text"
                        id="companyPhone"
                        value={companyPhone}
                        onChange={handleInputChange}
                        placeholder="+260 XXX XXX XXX"
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                        required
                      />
                    </div>

                    {/* Email */}
                    <div>
                      <label htmlFor="companyEmail" className="block text-sm font-medium text-gray-700 mb-2">
                        <div className="flex items-center">
                          <Mail className="w-4 h-4 mr-2 text-gray-400" />
                          Email Address *
                        </div>
                      </label>
                      <input
                        type="email"
                        id="companyEmail"
                        value={companyEmail}
                        onChange={handleInputChange}
                        placeholder="company@example.com"
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                        required
                      />
                    </div>

                    {/* Address */}
                    <div className="md:col-span-2">
                      <label htmlFor="companyAddress" className="block text-sm font-medium text-gray-700 mb-2">
                        <div className="flex items-center">
                          <MapPin className="w-4 h-4 mr-2 text-gray-400" />
                          Company Address *
                        </div>
                      </label>
                      <textarea
                        id="companyAddress"
                        value={companyAddress}
                        onChange={handleInputChange}
                        placeholder="Enter complete physical address"
                        rows="3"
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 resize-none"
                        required
                      />
                    </div>

                    {/* Logo Upload */}
                    <div className="md:col-span-2">
                      <label htmlFor="companyLogo" className="block text-sm font-medium text-gray-700 mb-2">
                        <div className="flex items-center">
                          <Image className="w-4 h-4 mr-2 text-gray-400" />
                          Company Logo (Optional)
                        </div>
                      </label>
                      
                      <div className="space-y-4">
                        {logoPreview && (
                          <div className="relative">
                            <div className="w-32 h-32 border-2 border-gray-200 rounded-xl overflow-hidden">
                              <img 
                                src={logoPreview} 
                                alt="Logo preview" 
                                className="w-full h-full object-contain p-2"
                              />
                            </div>
                            <button
                              type="button"
                              onClick={removeLogo}
                              className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        )}

                        <div className="relative">
                          <input
                            type="file"
                            id="companyLogo"
                            onChange={handleInputChange}
                            accept="image/*"
                            className="hidden"
                          />
                          <label
                            htmlFor="companyLogo"
                            className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-all duration-300"
                          >
                            <div className="flex flex-col items-center justify-center pt-5 pb-6">
                              <Upload className="w-8 h-8 text-gray-400 mb-2" />
                              <p className="text-sm text-gray-500">
                                <span className="font-medium text-blue-500">Click to upload</span> or drag and drop
                              </p>
                              <p className="text-xs text-gray-400 mt-1">
                                PNG, JPG, GIF up to 5MB
                              </p>
                            </div>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <div className="pt-6">
                    <button
                      type="submit"
                      disabled={!isFormValid || isLoading}
                      className={`w-full py-3.5 px-6 rounded-xl font-medium transition-all duration-300 ${
                        isFormValid && !isLoading
                          ? 'bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white shadow-lg hover:shadow-xl'
                          : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      {isLoading ? (
                        <div className="flex items-center justify-center">
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-3"></div>
                          Registering Company...
                        </div>
                      ) : (
                        <div className="flex items-center justify-center">
                          <Upload className="w-5 h-5 mr-2" />
                          Register Company
                        </div>
                      )}
                    </button>
                  </div>
                </form>
              </div>

              {/* Form Footer */}
              <div className="px-8 py-6 border-t border-gray-200 bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Globe className="w-4 h-4 text-blue-500" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">Fields marked with * are required</p>
                      <p className="text-xs text-gray-500">Logo upload is optional but recommended</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-500">
                      <span className="font-medium text-gray-900">3</span> of 4 steps completed
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Features Section */}
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-6 rounded-xl border border-blue-200">
                <h3 className="font-bold text-gray-900 mb-3 flex items-center">
                  <Building2 className="w-5 h-5 mr-2 text-blue-500" />
                  Why Register a Company?
                </h3>
                <ul className="space-y-2">
                  <li className="flex items-center text-sm text-gray-700">
                    <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                    Centralized management of all business locations
                  </li>
                  <li className="flex items-center text-sm text-gray-700">
                    <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                    Professional client-facing company profile
                  </li>
                  <li className="flex items-center text-sm text-gray-700">
                    <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                    Brand consistency across all properties
                  </li>
                </ul>
              </div>
              <div className="bg-gradient-to-r from-purple-50 to-purple-100 p-6 rounded-xl border border-purple-200">
                <h3 className="font-bold text-gray-900 mb-3 flex items-center">
                  <Image className="w-5 h-5 mr-2 text-purple-500" />
                  Logo Guidelines
                </h3>
                <ul className="space-y-2">
                  <li className="flex items-center text-sm text-gray-700">
                    <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                    Recommended size: 500x500 pixels
                  </li>
                  <li className="flex items-center text-sm text-gray-700">
                    <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                    PNG format with transparent background
                  </li>
                  <li className="flex items-center text-sm text-gray-700">
                    <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                    File size should not exceed 5MB
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default ModalCreateCompanyForm;